import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus } from './useDialogFocus';
import type { ClientSession } from '../api';
import {
  participationPath,
  participationRequest,
  participationProofKey,
  participationSignIn,
} from '../participation-api';
import './participation.css';

type Settings = {
  enabled: boolean;
  accountRequired: boolean;
  maxPartySize: number | null;
  deadline: string | null;
  pilot: boolean;
  version: number;
};
type Opportunity = {
  id: string;
  title: string;
  description: string;
  requirements: string;
  shiftStart: string | null;
  shiftEnd: string | null;
  capacity: number;
  remaining: number;
  unit: string;
  accountRequired: boolean;
  deadline: string | null;
  cancellationDeadline: string | null;
  closed: boolean;
  pilot: boolean;
  version: number;
};
type Signup = {
  id: string;
  opportunityId: string;
  quantity: number;
  name: string;
  status: string;
  needsReconfirmation: boolean;
  cancellationRequested?: boolean;
};
type State = {
  calendarId: string;
  canManage: boolean;
  canReadRsvps: boolean;
  canReadVolunteerRoster: boolean;
  rsvpAvailable: boolean;
  volunteerAvailable: boolean;
  guestAvailable?: boolean;
  closed: boolean;
  settings: Settings;
  ownRsvp: {
    id: string;
    name: string;
    count: number;
    additionalNames: string[];
    status: string;
    needsReconfirmation: boolean;
  } | null;
  opportunities: Opportunity[];
  ownSignups: Signup[];
};
type Props = {
  eventId: string;
  occurrence?: string | null;
  session: ClientSession | null;
  mode?: 'public' | 'manage' | 'report';
};
function GuestNotice({
  eventId,
  occurrence,
  available,
  session,
  verified,
}: Props & { available: boolean; verified: (name: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      if (!challengeId) {
        const result = await participationRequest<{ challengeId: string }>(
          '/api/participation/email/start',
          session,
          'POST',
          { eventId, occurrence: occurrence || '', email, name },
        );
        setChallengeId(result.challengeId);
      } else {
        const result = await participationRequest<{ proof: string; name: string }>(
          '/api/participation/email/verify',
          session,
          'POST',
          { challengeId, code },
        );
        sessionStorage.setItem(participationProofKey(eventId, occurrence), result.proof);
        await verified(result.name);
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="participation-guest">
      <summary>Respond without an account</summary>
      {available ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p>
            Verify your email before confirming an RSVP or volunteer place. This does not create an
            account or grant calendar membership.
          </p>
          {!challengeId ? (
            <>
              <label className="field">
                Your name
                <input
                  required
                  maxLength={160}
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="field">
                Email address
                <input
                  required
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            </>
          ) : (
            <>
              <p>
                {session?.capabilities.local
                  ? 'The verification code is in the local test email below. No email was sent externally.'
                  : `A verification code was sent to ${email}. Check your inbox.`}
              </p>
              {session?.capabilities.local && (
                <>
                  <button
                    className="button button-quiet"
                    type="button"
                    onClick={() => {
                      void participationRequest<{ messages: { subject: string; body: string }[] }>(
                        '/__local/mail-preview',
                        session,
                        'POST',
                        { email },
                      )
                        .then((result) =>
                          setLocalEmail(result.messages[0]?.body || 'No local email found yet.'),
                        )
                        .catch((cause) => setError(cause.message));
                    }}
                  >
                    Open local test email
                  </button>
                  {localEmail && <p className="participation-private-note">{localEmail}</p>}
                </>
              )}
              <label className="field">
                Verification code
                <input
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
            </>
          )}
          <button className="button" disabled={busy}>
            {busy ? 'Please wait…' : challengeId ? 'Verify email' : 'Send verification code'}
          </button>
        </form>
      ) : (
        <p>
          Email verification is unavailable for this event. Please sign in with an eligible account
          to respond. Browsing this event does not require an account.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
    </details>
  );
}
function VolunteerCard({
  opportunity,
  signup,
  session,
  path,
  available,
  reload,
  guestVerified,
  openIdentity,
}: {
  opportunity: Opportunity;
  signup?: Signup;
  session: ClientSession | null;
  path: (suffix: string) => string;
  available: boolean;
  reload: () => Promise<void>;
  guestVerified: boolean;
  openIdentity: () => void;
}) {
  const [name, setName] = useState(session?.user?.name || '');
  const [quantity, setQuantity] = useState(1);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const operation = useRef(crypto.randomUUID());
  const active = signup && signup.status === 'active';
  const save = async (cancel = false) => {
    setBusy(true);
    setError('');
    try {
      await participationRequest(
        path(cancel ? `/signups/${signup!.id}` : '/signup'),
        session,
        cancel ? 'DELETE' : 'POST',
        cancel
          ? undefined
          : { opportunityId: opportunity.id, quantity, name, idempotencyKey: operation.current },
      );
      await reload();
      setReview(false);
      setNotice(
        cancel
          ? 'Your signup is cancelled.'
          : 'Your place is confirmed. You can return here to cancel.',
      );
      operation.current = crypto.randomUUID();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="participation-opportunity">
      <h3>{opportunity.title}</h3>
      <p>{opportunity.description}</p>
      {opportunity.shiftStart && (
        <p>
          <time dateTime={opportunity.shiftStart}>
            {new Date(opportunity.shiftStart).toLocaleString()}
          </time>{' '}
          -{' '}
          {opportunity.shiftEnd && (
            <time dateTime={opportunity.shiftEnd}>
              {new Date(opportunity.shiftEnd).toLocaleString()}
            </time>
          )}
        </p>
      )}
      {opportunity.requirements && <p>What to know: {opportunity.requirements}</p>}
      <p>
        <strong>
          Remaining ({opportunity.unit || 'places'}): {opportunity.remaining}
        </strong>
      </p>
      {opportunity.deadline && <p>Sign up by {new Date(opportunity.deadline).toLocaleString()}.</p>}
      {opportunity.cancellationDeadline && (
        <p>
          Self-service cancellation until{' '}
          {new Date(opportunity.cancellationDeadline).toLocaleString()}.
        </p>
      )}
      {active ? (
        <>
          <p>
            Your signup: {signup.name} ({signup.quantity}).
          </p>
          {signup.needsReconfirmation && (
            <>
              <p role="status">
                The event or shift details changed. Please review the time and requirements above
                before confirming again.
              </p>
              {available && (
                <button
                  className="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void participationRequest(
                      path(`/signups/${signup.id}/reconfirm`),
                      session,
                      'POST',
                      {},
                    )
                      .then(async () => {
                        await reload();
                        setNotice('Your updated commitment is confirmed.');
                      })
                      .catch((cause) => setError(cause.message))
                      .finally(() => setBusy(false));
                  }}
                >
                  Confirm updated commitment
                </button>
              )}
            </>
          )}
          <div className="participation-actions">
            <button className="button button-quiet" disabled={busy} onClick={() => void save(true)}>
              Cancel signup
            </button>
            {signup.cancellationRequested ? (
              <p role="status">
                The organizer has been told you cannot attend. Your place stays reserved until they
                release it.
              </p>
            ) : (
              <button
                className="button button-quiet"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void participationRequest(
                    path(`/signups/${signup.id}/unavailable`),
                    session,
                    'POST',
                    {},
                  )
                    .then(async () => {
                      await reload();
                      setNotice('The organizer has been told you cannot attend.');
                    })
                    .catch((cause) => setError(cause.message))
                    .finally(() => setBusy(false));
                }}
              >
                Let organizer know I cannot attend
              </button>
            )}
          </div>
        </>
      ) : available &&
        !opportunity.closed &&
        (session?.user || (guestVerified && !opportunity.accountRequired)) ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (review) void save();
            else setReview(true);
          }}
        >
          <label className="field">
            Your name
            <input
              required
              maxLength={120}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setReview(false);
                operation.current = crypto.randomUUID();
              }}
            />
          </label>
          <label className="field">
            Quantity
            <input
              type="number"
              min={1}
              max={Math.max(1, opportunity.remaining)}
              required
              value={quantity}
              onChange={(e) => {
                setQuantity(Number(e.target.value));
                setReview(false);
                operation.current = crypto.randomUUID();
              }}
            />
          </label>
          {review && (
            <p>
              Confirm {quantity} {opportunity.unit || 'places'} for {name}. Availability may change
              until you confirm.
            </p>
          )}
          <button className="button" disabled={busy || opportunity.remaining < 1}>
            {busy ? 'Saving…' : review ? 'Confirm signup' : 'Review signup'}
          </button>
        </form>
      ) : (
        <p>
          {opportunity.closed || !available ? 'New signups are closed.' : 'Sign in to volunteer.'}
        </p>
      )}

      {available &&
        !opportunity.closed &&
        !session?.user &&
        (!guestVerified || opportunity.accountRequired) && (
          <button className="button" onClick={openIdentity}>
            Verify email or sign in to volunteer
          </button>
        )}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
    </article>
  );
}
export function EventParticipation({ eventId, occurrence, session, mode = 'public' }: Props) {
  const [state, setState] = useState<State>();
  const [dialog, setDialog] = useState<'rsvp' | 'volunteer' | 'identity' | null>(null);
  const [banner, setBanner] = useState<HTMLElement | null>(null);
  const dialogRef = useDialogFocus(() => setDialog(null), { enabled: Boolean(dialog) });
  useEffect(() => {
    if (mode === 'public')
      setBanner(document.querySelector<HTMLElement>('.event-page > .event-article > .event-hero'));
  }, [eventId, mode]);
  const [attachedForms, setAttachedForms] = useState<
    { id: string; eventId: string | null; title: string; path: string; canManage: boolean }[]
  >([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(session?.user?.name || '');
  const [count, setCount] = useState(1);
  const [names, setNames] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  const [guestVerified, setGuestVerified] = useState(
    Boolean(sessionStorage.getItem(participationProofKey(eventId, occurrence))),
  );
  const path = (suffix = '') => participationPath(eventId, occurrence, suffix);
  const reload = async () => {
    const value = await participationRequest<State>(path(), session);
    setState(value);
    return value;
  };
  useEffect(() => {
    let alive = true;
    participationRequest<State>(path(), session)
      .then((value) => {
        if (!alive) return;
        setState(value);
        if (value.ownRsvp) {
          setName(value.ownRsvp.name);
          setCount(value.ownRsvp.count);
          setNames(value.ownRsvp.additionalNames);
        }
      })
      .catch((cause) => {
        if (alive) setError(cause.message);
      });
    return () => {
      alive = false;
    };
  }, [eventId, occurrence, session?.user?.id]);
  useEffect(() => {
    let active = true;
    void participationRequest<{
      forms: {
        id: string;
        eventId: string | null;
        title: string;
        path: string;
        canManage: boolean;
      }[];
    }>(`/api/participation/forms?eventId=${encodeURIComponent(eventId)}`, session)
      .then((result) => {
        if (active) setAttachedForms(result.forms.filter((form) => form.eventId === eventId));
      })
      .catch(() => {
        if (active) setAttachedForms([]);
      });
    return () => {
      active = false;
    };
  }, [eventId, session?.user?.id]);
  const mutate = async (suffix: string, method: string, body?: unknown, message = 'Saved.') => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await participationRequest(path(suffix), session, method, body);
      await reload();
      setEditing(false);
      setNotice(message);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!state)
    return error ? (
      <section className="event-participation">
        <p role="alert">{error}</p>
      </section>
    ) : null;
  if (mode === 'report')
    return (
      <section className="event-participation participation-management">
        <VolunteerReaders
          path={path}
          session={session}
          onSaved={async () => {
            await reload();
          }}
        />
        {state.canReadRsvps || state.canReadVolunteerRoster ? (
          <PrivateRosters state={state} path={path} session={session} />
        ) : (
          <p role="alert">
            You do not have access to private attendance information for this event.
          </p>
        )}
      </section>
    );
  if (mode === 'manage')
    return (
      <section
        className="event-participation participation-management"
        aria-label="Manage event participation"
      >
        {state.canManage && (
          <>
            <section className="participation-card">
              <h2>Polls and feedback for this event</h2>
              <a
                className="button"
                href={`/participation/new?event=${encodeURIComponent(eventId)}&calendar=${encodeURIComponent(state.calendarId)}`}
              >
                Create an event poll or feedback form
              </a>
            </section>
            <OrganizerSettings state={state} busy={busy} save={mutate} />
          </>
        )}
        {(state.canReadRsvps || state.canReadVolunteerRoster) && (
          <a
            className="button button-quiet"
            href={`/admin/participation/events/${encodeURIComponent(eventId)}${occurrence ? `?occurrence=${encodeURIComponent(occurrence)}` : ''}`}
          >
            Open attendance in administration
          </a>
        )}
        {error && (
          <p role="alert" className="inline-error">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="inline-success">
            {notice}
          </p>
        )}
      </section>
    );
  const active = state.ownRsvp && state.ownRsvp.status === 'active';
  if (
    !state.rsvpAvailable &&
    !state.volunteerAvailable &&
    !state.canManage &&
    !state.ownRsvp &&
    !state.ownSignups.length &&
    !attachedForms.length
  )
    return null;
  return (
    <section className="event-participation" aria-label="Take part in this event">
      {attachedForms.length > 0 && (
        <section className="participation-card">
          <h2>Polls and feedback</h2>
          <ul>
            {attachedForms.map((form) => (
              <li key={form.id}>
                <a href={form.path}>{form.title}</a>
                {form.canManage && (
                  <>
                    {' '}
                    · <a href={`/participation/${form.id}/manage`}>Manage</a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {banner &&
        (state.rsvpAvailable || (state.volunteerAvailable && state.opportunities.length > 0)) &&
        createPortal(
          <div className="participation-banner-actions" role="group" aria-label="Take part">
            {state.rsvpAvailable && (
              <button
                className="button"
                onClick={() => {
                  setNotice('');
                  setError('');
                  setDialog('rsvp');
                }}
              >
                {active ? 'Manage RSVP' : 'RSVP'}
              </button>
            )}
            {state.volunteerAvailable && state.opportunities.length > 0 && (
              <button
                className="button"
                onClick={() => {
                  setNotice('');
                  setError('');
                  setDialog('volunteer');
                }}
              >
                Volunteer
              </button>
            )}
          </div>,
          banner,
        )}
      {!state.volunteerAvailable &&
        state.ownSignups.some((signup) => signup.status === 'active') && (
          <button
            className="button button-quiet"
            onClick={() => {
              setNotice('');
              setError('');
              setDialog('volunteer');
            }}
          >
            Manage my existing volunteer signup
          </button>
        )}
      {!state.rsvpAvailable && state.ownRsvp && state.ownRsvp.status !== 'withdrawn' && (
        <button
          className="button button-quiet"
          onClick={() => {
            setNotice('');
            setError('');
            setDialog('rsvp');
          }}
        >
          Manage my existing RSVP
        </button>
      )}
      {dialog &&
        createPortal(
          <div
            className="modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setDialog(null);
            }}
          >
            <section
              ref={dialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-labelledby="participation-dialog-title"
              className="participation-dialog"
            >
              <header className="modal-header">
                <h2 id="participation-dialog-title">
                  {dialog === 'rsvp' ? 'RSVP' : dialog === 'volunteer' ? 'Volunteer' : 'Join in'}
                </h2>
                <button
                  className="icon-button"
                  aria-label={
                    dialog === 'rsvp'
                      ? 'Close RSVP'
                      : dialog === 'volunteer'
                        ? 'Close Volunteer'
                        : 'Close participation sign in'
                  }
                  onClick={() => setDialog(null)}
                >
                  ×
                </button>
              </header>
              <div className="participation-dialog-scroll">
                {!session?.user && !guestVerified && (
                  <section className="participation-card">
                    <h2>Join in</h2>
                    <button
                      className="button"
                      onClick={() => {
                        void participationSignIn('event', eventId, session).catch((cause) =>
                          setError(cause.message),
                        );
                      }}
                    >
                      Sign in to respond
                    </button>
                    <GuestNotice
                      eventId={eventId}
                      occurrence={occurrence ?? null}
                      session={session}
                      available={
                        Boolean(state.guestAvailable) &&
                        ((!state.settings.accountRequired && state.rsvpAvailable) ||
                          state.opportunities.some(
                            (opportunity) => !opportunity.accountRequired && !opportunity.closed,
                          ))
                      }
                      verified={async (guestName) => {
                        setName(guestName);
                        setGuestVerified(true);
                        await reload();
                        if (dialog === 'identity') setDialog(null);
                      }}
                    />
                  </section>
                )}
                {guestVerified && !session?.user && (
                  <p role="status">Your email is verified for this event.</p>
                )}
                {dialog === 'rsvp' && (
                  <>
                    {(state.rsvpAvailable || state.ownRsvp || state.canManage) && (
                      <section className="participation-card">
                        <p>
                          Your family name, headcount and optional names are private to authorized
                          staff and calendar managers.
                        </p>
                        {active && !editing ? (
                          <>
                            <p>
                              You are coming: <strong>{state.ownRsvp!.name}</strong>,{' '}
                              {state.ownRsvp!.count}{' '}
                              {state.ownRsvp!.count === 1 ? 'person' : 'people'}.
                            </p>
                            {state.ownRsvp!.additionalNames.length > 0 && (
                              <p>{state.ownRsvp!.additionalNames.join(', ')}</p>
                            )}
                            {state.ownRsvp!.needsReconfirmation && (
                              <p role="status">
                                The event time changed. Review the schedule and confirm your RSVP
                                again.
                              </p>
                            )}
                            <div className="participation-actions">
                              <button
                                className="button"
                                disabled={state.closed || !state.rsvpAvailable}
                                onClick={() => setEditing(true)}
                              >
                                Change RSVP
                              </button>
                              <button
                                className="button button-quiet"
                                disabled={busy}
                                onClick={() =>
                                  void mutate(
                                    '/rsvp',
                                    'DELETE',
                                    undefined,
                                    'Your RSVP is cancelled.',
                                  )
                                }
                              >
                                Cancel RSVP
                              </button>
                            </div>
                          </>
                        ) : state.rsvpAvailable &&
                          !state.closed &&
                          (session?.user || (guestVerified && !state.settings.accountRequired)) ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              void mutate(
                                '/rsvp',
                                'PUT',
                                {
                                  name,
                                  count,
                                  additionalNames: names.map((item) => item.trim()).filter(Boolean),
                                },
                                'Your RSVP is confirmed.',
                              );
                            }}
                          >
                            <label className="field">
                              Name or family name
                              <input
                                required
                                maxLength={120}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="The Smith family"
                              />
                            </label>
                            <label className="field">
                              Number coming, including you
                              <input
                                type="number"
                                required
                                min={1}
                                max={state.settings.maxPartySize ?? undefined}
                                value={count}
                                onChange={(e) => setCount(Number(e.target.value))}
                              />
                            </label>
                            <p>
                              Additional names are optional. You do not need to name children or
                              other guests.
                            </p>
                            {names.map((item, index) => (
                              <div className="participation-inline" key={index}>
                                <label className="field">
                                  Additional name {index + 1}
                                  <input
                                    maxLength={120}
                                    value={item}
                                    onChange={(e) =>
                                      setNames(
                                        names.map((value, i) =>
                                          i === index ? e.target.value : value,
                                        ),
                                      )
                                    }
                                  />
                                </label>
                                <button
                                  type="button"
                                  className="button button-quiet"
                                  aria-label={`Remove additional name ${index + 1}`}
                                  onClick={() => setNames(names.filter((_, i) => i !== index))}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            {names.length < Math.min(count - 1, 100) && (
                              <button
                                type="button"
                                className="button button-quiet"
                                onClick={() => setNames([...names, ''])}
                              >
                                Add an optional name
                              </button>
                            )}
                            {names.filter((item) => item.trim()).length > count - 1 && (
                              <p role="alert">Remove extra names or increase the number coming.</p>
                            )}
                            <button
                              className="button"
                              disabled={
                                busy || names.filter((item) => item.trim()).length > count - 1
                              }
                            >
                              {busy ? 'Saving…' : active ? 'Save RSVP changes' : 'Confirm RSVP'}
                            </button>
                          </form>
                        ) : (
                          <p>
                            {state.closed || !state.rsvpAvailable
                              ? 'RSVP is currently closed.'
                              : 'Sign in to RSVP.'}
                          </p>
                        )}
                      </section>
                    )}
                  </>
                )}
                {dialog === 'volunteer' && (
                  <>
                    {(state.volunteerAvailable ||
                      state.opportunities.length > 0 ||
                      state.canManage) && (
                      <section className="participation-card">
                        <p>
                          Choose how you would like to help. Your name is visible only to authorized
                          organizers.
                        </p>
                        {state.opportunities.map((opportunity) => (
                          <VolunteerCard
                            key={opportunity.id}
                            opportunity={opportunity}
                            {...(state.ownSignups.find(
                              (item) =>
                                item.opportunityId === opportunity.id && item.status === 'active',
                            )
                              ? {
                                  signup: state.ownSignups.find(
                                    (item) =>
                                      item.opportunityId === opportunity.id &&
                                      item.status === 'active',
                                  )!,
                                }
                              : {})}
                            session={session}
                            guestVerified={guestVerified}
                            openIdentity={() => {
                              const guest = dialogRef.current?.querySelector(
                                'details.participation-guest',
                              );
                              guest?.setAttribute('open', '');
                              guest?.scrollIntoView({ block: 'nearest' });
                            }}
                            path={path}
                            available={state.volunteerAvailable && !state.closed}
                            reload={async () => {
                              await reload();
                            }}
                          />
                        ))}
                        {!state.opportunities.length && (
                          <p>No volunteer opportunities have been added.</p>
                        )}
                      </section>
                    )}
                    {state.ownSignups
                      .filter(
                        (signup) =>
                          signup.status === 'active' &&
                          !state.opportunities.some(
                            (opportunity) => opportunity.id === signup.opportunityId,
                          ),
                      )
                      .map((signup) => (
                        <section className="participation-card" key={signup.id}>
                          <h2>Your existing volunteer commitment</h2>
                          <p>
                            {signup.name}: {signup.quantity}. New signups are unavailable, but you
                            can manage your commitment.
                          </p>
                          <button
                            className="button button-quiet"
                            disabled={busy}
                            onClick={() =>
                              void mutate(
                                `/signups/${signup.id}`,
                                'DELETE',
                                undefined,
                                'Your signup is cancelled.',
                              )
                            }
                          >
                            Cancel existing signup
                          </button>
                          <button
                            className="button button-quiet"
                            disabled={busy}
                            onClick={() =>
                              void mutate(
                                `/signups/${signup.id}/unavailable`,
                                'POST',
                                {},
                                'The organizer has been told you cannot attend.',
                              )
                            }
                          >
                            Let organizer know I cannot attend
                          </button>
                        </section>
                      ))}
                  </>
                )}
                {error && (
                  <p role="alert" className="inline-error">
                    {error}
                  </p>
                )}
                {notice && (
                  <p role="status" className="inline-success">
                    {notice}
                  </p>
                )}
              </div>
            </section>
          </div>,
          document.body,
        )}
      {!dialog && error && (
        <p role="alert" className="inline-error">
          {error}
        </p>
      )}
      {!dialog && notice && (
        <p role="status" className="inline-success">
          {notice}
        </p>
      )}
    </section>
  );
}
function OrganizerSettings({
  state,
  busy,
  save,
}: {
  state: State;
  busy: boolean;
  save: (suffix: string, method: string, body?: unknown, notice?: string) => Promise<void>;
}) {
  const [settings, setSettings] = useState(state.settings);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState(1);
  const [unit, setUnit] = useState('places');
  const [accountRequired, setAccountRequired] = useState(true);
  const [pilot, setPilot] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [cancellationDeadline, setCancellationDeadline] = useState('');
  const [shiftStart, setShiftStart] = useState('');
  const [shiftEnd, setShiftEnd] = useState('');
  const [requirements, setRequirements] = useState('');
  useEffect(() => setSettings(state.settings), [state.settings]);
  return (
    <>
      <details className="participation-card">
        <summary>RSVP settings</summary>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save('/settings', 'PATCH', settings, 'RSVP settings saved.');
          }}
        >
          <h3>RSVP settings</h3>
          <label>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />{' '}
            Enable RSVP for this event
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.accountRequired}
              onChange={(e) => setSettings({ ...settings, accountRequired: e.target.checked })}
            />{' '}
            Account required
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.maxPartySize === null}
              onChange={(event) =>
                setSettings({ ...settings, maxPartySize: event.target.checked ? null : 20 })
              }
            />{' '}
            No limit
          </label>
          <label className="field">
            Maximum party size
            <input
              type="number"
              min={1}
              disabled={settings.maxPartySize === null}
              value={settings.maxPartySize ?? ''}
              onChange={(e) => setSettings({ ...settings, maxPartySize: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            RSVP deadline (optional)
            <input
              type="datetime-local"
              value={
                settings.deadline
                  ? new Date(
                      new Date(settings.deadline).getTime() -
                        new Date(settings.deadline).getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .slice(0, 16)
                  : ''
              }
              onChange={(e) =>
                setSettings({
                  ...settings,
                  deadline: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.pilot}
              onChange={(e) => setSettings({ ...settings, pilot: e.target.checked })}
            />{' '}
            Staff pilot for this event
          </label>
          <button className="button" disabled={busy}>
            Save RSVP settings
          </button>
        </form>
      </details>
      <details className="participation-card">
        <summary>Volunteer setup</summary>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save(
              '/opportunities',
              'POST',
              {
                title,
                description,
                capacity,
                unit,
                requirements,
                shiftStart: shiftStart ? new Date(shiftStart).toISOString() : null,
                shiftEnd: shiftEnd ? new Date(shiftEnd).toISOString() : null,
                accountRequired,
                pilot,
                closed: false,
                deadline: deadline ? new Date(deadline).toISOString() : null,
                cancellationDeadline: cancellationDeadline
                  ? new Date(cancellationDeadline).toISOString()
                  : null,
              },
              'Volunteer opportunity added.',
            );
          }}
        >
          <h3>Add a volunteer opportunity</h3>
          <div className="participation-inline">
            <label className="field">
              Shift start (optional)
              <input
                type="datetime-local"
                value={shiftStart}
                onChange={(e) => setShiftStart(e.target.value)}
              />
            </label>
            <label className="field">
              Shift end (optional)
              <input
                type="datetime-local"
                required={Boolean(shiftStart)}
                value={shiftEnd}
                onChange={(e) => setShiftEnd(e.target.value)}
              />
            </label>
          </div>
          <label className="field">
            Requirements (optional)
            <textarea
              value={requirements}
              maxLength={4000}
              onChange={(e) => setRequirements(e.target.value)}
            />
          </label>
          <label className="field">
            Opportunity title
            <input
              required
              maxLength={160}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="field">
            What help is needed?
            <textarea
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="field">
            Capacity
            <input
              type="number"
              min={1}
              max={10000}
              value={capacity}
              onChange={(e) => setCapacity(Number(e.target.value))}
            />
          </label>
          <label className="field">
            Unit label
            <input required maxLength={40} value={unit} onChange={(e) => setUnit(e.target.value)} />
          </label>
          <label>
            <input
              type="checkbox"
              checked={accountRequired}
              onChange={(e) => setAccountRequired(e.target.checked)}
            />{' '}
            Account required for this opportunity
          </label>
          <p>
            Private events always require an account. Public guest signup also needs email
            verification to be available.
          </p>
          <label>
            <input type="checkbox" checked={pilot} onChange={(e) => setPilot(e.target.checked)} />{' '}
            Staff pilot opportunity
          </label>
          <label className="field">
            Signup deadline (optional)
            <input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </label>
          <label className="field">
            Cancellation deadline (optional)
            <input
              type="datetime-local"
              value={cancellationDeadline}
              onChange={(e) => setCancellationDeadline(e.target.value)}
            />
          </label>
          <button className="button" disabled={busy}>
            Add opportunity
          </button>
        </form>
        {state.opportunities.map((opportunity) => (
          <OpportunitySettings
            key={opportunity.id}
            opportunity={opportunity}
            busy={busy}
            save={save}
          />
        ))}
      </details>
    </>
  );
}
function OpportunitySettings({
  opportunity,
  busy,
  save,
}: {
  opportunity: Opportunity;
  busy: boolean;
  save: (suffix: string, method: string, body?: unknown, notice?: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(opportunity);
  useEffect(() => setDraft(opportunity), [opportunity]);
  return (
    <details className="participation-opportunity">
      <summary>Edit {opportunity.title}</summary>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const { remaining: _remaining, id: _id, ...input } = draft;
          void save(`/opportunities/${opportunity.id}`, 'PATCH', input, 'Opportunity updated.');
        }}
      >
        <label className="field">
          Opportunity title
          <input
            required
            maxLength={160}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </label>
        <label className="field">
          Description
          <textarea
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            maxLength={4000}
          />
        </label>
        <label className="field">
          Capacity
          <input
            type="number"
            min={1}
            max={10000}
            required
            value={draft.capacity}
            onChange={(e) => setDraft({ ...draft, capacity: Number(e.target.value) })}
          />
        </label>
        <label className="field">
          Requirements
          <textarea
            maxLength={4000}
            value={draft.requirements || ''}
            onChange={(e) => setDraft({ ...draft, requirements: e.target.value })}
          />
        </label>
        {(['shiftStart', 'shiftEnd', 'deadline', 'cancellationDeadline'] as const).map((field) => (
          <label className="field" key={field}>
            {
              {
                shiftStart: 'Shift start',
                shiftEnd: 'Shift end',
                deadline: 'Signup deadline',
                cancellationDeadline: 'Cancellation deadline',
              }[field]
            }
            <input
              type="datetime-local"
              value={
                draft[field]
                  ? new Date(
                      new Date(draft[field]!).getTime() -
                        new Date(draft[field]!).getTimezoneOffset() * 60000,
                    )
                      .toISOString()
                      .slice(0, 16)
                  : ''
              }
              onChange={(e) =>
                setDraft({
                  ...draft,
                  [field]: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
            />
          </label>
        ))}
        <label className="field">
          Unit label
          <input
            value={draft.unit}
            required
            maxLength={80}
            onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.accountRequired}
            onChange={(e) => setDraft({ ...draft, accountRequired: e.target.checked })}
          />{' '}
          Account required for this opportunity
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.pilot}
            onChange={(e) => setDraft({ ...draft, pilot: e.target.checked })}
          />{' '}
          Staff pilot opportunity
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.closed}
            onChange={(e) => setDraft({ ...draft, closed: e.target.checked })}
          />{' '}
          Closed to new signups
        </label>
        <button className="button" disabled={busy}>
          Save opportunity
        </button>
      </form>
    </details>
  );
}
function PrivateRosters({
  state,
  path,
  session,
}: {
  state: State;
  path: (suffix: string) => string;
  session: ClientSession | null;
}) {
  const [rows, setRows] = useState<Record<string, unknown>[]>();
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState('');
  const [error, setError] = useState('');
  const read = async (kind: string) => {
    try {
      const result = await participationRequest<{ rows: Record<string, unknown>[]; total: number }>(
        path(`/${kind}`),
        session,
      );
      setRows(result.rows);
      setKind(kind);
      setTotal(result.total);
      setError('');
    } catch (cause) {
      setError((cause as Error).message);
    }
  };
  return (
    <details className="participation-card">
      <summary>Private organizer lists</summary>
      <p>These records are private. Share exports only with authorized organizers.</p>
      <div className="participation-actions">
        {(['rsvps', 'volunteers'] as const)
          .filter((kind) => (kind === 'rsvps' ? state.canReadRsvps : state.canReadVolunteerRoster))
          .map((kind) => (
            <span key={kind}>
              <button className="button button-quiet" onClick={() => void read(kind)}>
                View {kind === 'rsvps' ? 'RSVPs' : 'volunteers'}
              </button>{' '}
              <a
                href={`${path(`/${kind}`)}${path(`/${kind}`).includes('?') ? '&' : '?'}format=csv`}
              >
                Export {kind === 'rsvps' ? 'RSVPs' : 'volunteers'}
              </a>
            </span>
          ))}
      </div>
      {rows && (
        <>
          <p>Total: {total}</p>
          <ul>
            {rows.map((row, index) => (
              <li key={index}>
                {String(row.name || row.displayName || '')} ·{' '}
                {String(row.count ?? row.quantity ?? '')}{' '}
                {Array.isArray(row.additionalNames) ? row.additionalNames.join(', ') : ''} ·{' '}
                {String(row.status || '')}
                {kind === 'volunteers' && state.canManage && row.status === 'active' && (
                  <button
                    className="button button-quiet"
                    onClick={() => {
                      void participationRequest(
                        path(`/volunteers/${String(row.id)}`),
                        session,
                        'DELETE',
                      )
                        .then(() => read('volunteers'))
                        .catch((cause) => setError(cause.message));
                    }}
                  >
                    Release signup
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </details>
  );
}

function VolunteerReaders({
  path,
  session,
  onSaved,
}: {
  path: (suffix: string) => string;
  session: ClientSession | null;
  onSaved: () => Promise<void>;
}) {
  const [readers, setReaders] = useState<
    { id: string; name: string; email: string; selected: boolean }[] | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    void participationRequest<{ candidates: NonNullable<typeof readers> }>(
      path('/volunteer-readers'),
      session,
    )
      .then(({ candidates }) => {
        if (active) setReaders(candidates);
      })
      .catch(() => {
        if (active) setReaders(null);
      });
    return () => {
      active = false;
    };
  }, [path('/volunteer-readers'), session?.user?.id]);
  if (!readers) return null;
  return (
    <details className="participation-card">
      <summary>Who can view volunteer signups</summary>
      <p>
        Choose authorized staff or calendar members who need names and signup details. Access
        applies to this event.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          setMessage('');
          void participationRequest(path('/volunteer-readers'), session, 'PUT', {
            userIds: readers.filter((reader) => reader.selected).map((reader) => reader.id),
          })
            .then(async () => {
              setMessage('Volunteer list access saved.');
              await onSaved();
            })
            .catch((cause) => setError((cause as Error).message))
            .finally(() => setBusy(false));
        }}
      >
        {readers.map((reader) => (
          <label key={reader.id}>
            <input
              type="checkbox"
              checked={reader.selected}
              onChange={(event) =>
                setReaders(
                  readers.map((item) =>
                    item.id === reader.id ? { ...item, selected: event.target.checked } : item,
                  ),
                )
              }
            />{' '}
            {reader.name} ({reader.email})
          </label>
        ))}
        {!readers.length && <p>No eligible readers are available.</p>}
        <button className="button" disabled={busy}>
          Save volunteer list access
        </button>
        {message && <p role="status">{message}</p>}
        {error && <p role="alert">{error}</p>}
      </form>
    </details>
  );
}
